import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as murajaahApi from '@/features/murajaah/api';
import type {
  CompleteSessionInput,
  CompleteSessionResult,
  MurajaahQueueRow,
  MurajaahSessionStatus,
  MurajaahSessionView,
  ScheduleSessionInput,
} from '@/features/murajaah/types';
import { useActiveOrganization } from '@/features/organization/active-organization-context';
import type { ApiError } from '@/lib/api/client';
import type { PagedData } from '@/lib/api/types';
import { TENANT_QUERY_PREFIX } from '@/lib/api/tenant-query';

/** Tenant-scoped query keys embed the Active Organization id AND the relevant student/session id (ADR-026 Addendum, AO-9). */
const sessionsListKey = (
  organizationId: string | null,
  studentId: string,
  page: number,
  limit: number,
) => [TENANT_QUERY_PREFIX, organizationId, 'murajaah', 'list', studentId, page, limit] as const;
const sessionDetailKey = (organizationId: string | null, sessionId: string) =>
  [TENANT_QUERY_PREFIX, organizationId, 'murajaah', 'detail', sessionId] as const;
const queueListKey = (organizationId: string | null, page: number, limit: number) =>
  [TENANT_QUERY_PREFIX, organizationId, 'murajaah', 'queue', page, limit] as const;

export function useMurajaahSessions(
  studentId: string,
  page = 1,
  limit = 20,
  status?: MurajaahSessionStatus,
): UseQueryResult<PagedData<MurajaahSessionView>, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: sessionsListKey(activeOrganizationId, studentId, page, limit),
    queryFn: () => murajaahApi.listSessions(studentId, page, limit, status),
    enabled: activeOrganizationId !== null && studentId.length > 0,
  });
}

/**
 * `enabled` defaults to `true` for backward compatibility, but every call
 * site should pass the caller's own `murajaah_sessions:manage` capability
 * check here too - the backend already rejects a non-manage caller
 * (ADR-022 Addendum, Decision 7.5), but firing a request that is
 * guaranteed to 403 is still wasted work worth skipping client-side.
 */
export function useMurajaahQueue(
  page = 1,
  limit = 20,
  enabled = true,
): UseQueryResult<PagedData<MurajaahQueueRow>, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: queueListKey(activeOrganizationId, page, limit),
    queryFn: () => murajaahApi.listQueue(page, limit),
    enabled: activeOrganizationId !== null && enabled,
  });
}

export function useScheduleSession(
  studentId: string,
): UseMutationResult<MurajaahSessionView, ApiError, ScheduleSessionInput> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: ScheduleSessionInput) => murajaahApi.scheduleSession(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'murajaah', 'list', studentId],
      });
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'murajaah', 'queue'],
      });
    },
  });
}

export function useCancelSession(studentId: string): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (sessionId: string) => murajaahApi.cancelSession(sessionId),
    onSuccess: (_data, sessionId) => {
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'murajaah', 'list', studentId],
      });
      void queryClient.invalidateQueries({
        queryKey: sessionDetailKey(activeOrganizationId, sessionId),
      });
    },
  });
}

export function useCompleteSession(
  studentId: string,
  sessionId: string,
): UseMutationResult<CompleteSessionResult, ApiError, CompleteSessionInput> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: CompleteSessionInput) => murajaahApi.completeSession(sessionId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'murajaah', 'list', studentId],
      });
      void queryClient.invalidateQueries({
        queryKey: sessionDetailKey(activeOrganizationId, sessionId),
      });
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'murajaah', 'queue'],
      });
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'progress', studentId],
      });
    },
  });
}
